import React, { useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../contexts/SessionContext';
import { Tool } from '../../interfaces';
import useToolAdd from '../../hooks/useToolAdd';
import './Tools.css';

interface ToolsAddControlProps {
  show: boolean;
  handleClose: () => void;
}

const ToolsAddModal: React.FC<ToolsAddControlProps> = ({ show, handleClose }) => {
  const { dispatch } = useSession();
  const { t } = useTranslation();
  const { toolAdd, error: submitError } = useToolAdd();
  const [urlError, setUrlError] = useState("");

  const [toolData, setToolData] = useState<Tool>({
    id: 0,
    isIntern: false,
    name: '',
    description: '',
    url: '',
    image: ''
  });

  const handleChange = (e: React.ChangeEvent<{ name?: string; value: string }>) => {
    const { name, value } = e.target;

    setToolData(prevState => ({
      ...prevState,
      [name as string]: value,
    }));
    if (name === "url" && !/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i.test(value)) {
      setUrlError(t("tools.invalidUrl"));
    } else {
      setUrlError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = await toolAdd(toolData);
    if (urlError) {
      return;
    }
    if (data) {
      dispatch({ type: 'TOOL_ADD', payload: { tool: data } });
      handleClose();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header>
        <Modal.Title className="text-secondary text-center fs-2 fw-semibold position-relative top-0 start-50 translate-middle-x">{t('tools.title')}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} >
        <Modal.Body>
          <Row className="g-4">
            <Col md={6}>
              <Form.Group controlId="formName" className="mb-0 text-nowrap">
                <Form.Label className="fs-5 fw-semibold text-secondary">{t("name")}</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={toolData.name}
                  onChange={handleChange}
                  placeholder={t("tools.enterName")}
                  className='text-secondary bg-light fw-medium'
                  maxLength={20}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formURL" className="mb-0 text-nowrap">
                <Form.Label className="fs-5 fw-semibold text-secondary">URL</Form.Label>
                <Form.Control
                  type="text"
                  name="url"
                  value={toolData.url}
                  onChange={handleChange}
                  placeholder="https://..."
                  className='text-secondary bg-light fw-medium'
                  required
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group controlId="formDescription" className="mb-0 text-nowrap">
                <Form.Label className="fs-5 fw-semibold text-secondary">{t("description")}</Form.Label>
                <br />
                <textarea
                  name="description"
                  value={toolData.description}
                  onChange={handleChange}
                  placeholder={t("tools.enterDescription")}
                  className='text-secondary bg-light fw-medium border-0 w-100 rounded-2 ps-2'
                  minLength={5}
                  maxLength={150}
                  required
                />
              </Form.Group>
            </Col>
            {submitError && <Alert variant="danger">{submitError}</Alert>}
            {urlError && <Alert variant='danger' className="fw-medium fs-6 text-center p-2">{urlError}</Alert>}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="py-2 px-3 fs-5" type="submit">{t("submit")}</Button>
          <Button variant="primary" className="py-2 px-3 fs-5" onClick={handleClose}>{t("close")}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ToolsAddModal;
